import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildAttachmentPayload, getAttachmentKind, isVideoTooLong, uploadFiles } from '@/lib/upload';
import type { AttachmentItem, AttachmentPayload, UploadedAttachmentResult } from '@/types/attachment';
import { useVideoDuration } from '@/hooks/useVideoDuration';

function createAttachmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useFileUpload() {
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { getVideoDuration } = useVideoDuration();

  const revokePreviewUrl = useCallback((attachment: AttachmentItem) => {
    if (attachment.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
  }, []);

  useEffect(() => {
    return () => {
      attachments.forEach(revokePreviewUrl);
    };
  }, [attachments, revokePreviewUrl]);

  const addFiles = useCallback(async (incomingFiles: FileList | File[]) => {
    const fileList = Array.from(incomingFiles);
    if (fileList.length === 0) return;

    setUploadError(null);

    const prepared = await Promise.all(
      fileList.map(async (file) => {
        const kind = getAttachmentKind(file);
        const attachment: AttachmentItem = {
          id: createAttachmentId(),
          file,
          kind,
        };

        if (kind === 'image') {
          attachment.previewUrl = URL.createObjectURL(file);
          return attachment;
        }

        if (kind === 'video') {
          attachment.previewUrl = URL.createObjectURL(file);
          try {
            const duration = await getVideoDuration(file);
            attachment.duration = duration;
            if (isVideoTooLong(duration)) {
              attachment.validationError = 'Les vidéos de plus de 3 minutes ne sont pas autorisées.';
            }
          } catch {
            attachment.validationError = 'Impossible de lire la durée de cette vidéo.';
          }
          return attachment;
        }

        return attachment;
      }),
    );

    setAttachments((prev) => [...prev, ...prepared]);
  }, [getVideoDuration]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const next = prev.filter((attachment) => attachment.id !== id);
      const removed = prev.find((attachment) => attachment.id === id);
      if (removed) {
        revokePreviewUrl(removed);
      }
      return next;
    });
  }, [revokePreviewUrl]);

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach(revokePreviewUrl);
      return [];
    });
  }, [revokePreviewUrl]);

  const uploadAttachments = useCallback(async (): Promise<AttachmentPayload[]> => {
    const validAttachments = attachments.filter((attachment) => !attachment.validationError);
    if (validAttachments.length === 0) {
      return [];
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const uploadedResults = await uploadFiles(
        validAttachments.map((attachment) => attachment.file),
        (percent) => setUploadProgress(percent),
      );

      const payloads = validAttachments.map((attachment, index) => {
        const result = uploadedResults[index] as UploadedAttachmentResult | undefined;
        if (!result) {
          throw new Error('Upload failed');
        }
        return buildAttachmentPayload(result, attachment.kind, attachment.duration);
      });

      clearAttachments();
      setUploadProgress(100);
      return payloads;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Impossible d’envoyer ces fichiers.';
      setUploadError(message);
      return [];
    } finally {
      setIsUploading(false);
    }
  }, [attachments, clearAttachments]);

  const hasValidAttachments = useMemo(() => attachments.some((attachment) => !attachment.validationError), [attachments]);

  return {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    uploadAttachments,
    isUploading,
    uploadProgress,
    uploadError,
    hasValidAttachments,
    dragActive,
    setDragActive,
  };
}
